import { useState, useEffect } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CalendarIcon } from "lucide-react";
import { api, fmtDate, addDays } from "@/lib/api";
import { es } from "date-fns/locale";

/**
 * Calendar input that marks days as full/holiday/weekend in red, based on capacity.
 *
 * Props:
 *   value: string YYYY-MM-DD
 *   onChange: (string) => void
 *   capacityEndpoint: "/capacity" or "/quick-capacity"
 *   neededHours: number — the day is "full" if available < neededHours
 *   testid: string
 *   horizonDays: int (default 60)
 */
export default function CapacityCalendar({ value, onChange, capacityEndpoint = "/capacity", neededHours = 0.5, testid = "capacity-calendar", horizonDays = 60 }) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState([]);

  useEffect(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const from = fmtDate(start);
    const to = fmtDate(addDays(start, horizonDays - 1));
    api.get(`${capacityEndpoint}?from_date=${from}&to_date=${to}`).then((res) => {
      setDays(res.data.days || []);
    }).catch(() => setDays([]));
  }, [capacityEndpoint, horizonDays, open]);

  const fullDates = days
    .filter((d) => !d.is_workday || d.is_holiday || d.available < neededHours)
    .map((d) => {
      const [y, m, day] = d.date.split("-");
      return new Date(Number(y), Number(m) - 1, Number(day));
    });

  const selectedDate = value ? (() => {
    const [y, m, day] = value.split("-");
    return new Date(Number(y), Number(m) - 1, Number(day));
  })() : undefined;

  const display = value
    ? new Date(value.split("-")[0], Number(value.split("-")[1]) - 1, value.split("-")[2]).toLocaleDateString("es-ES", { weekday: "long", day: "2-digit", month: "long" })
    : "Selecciona fecha";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-11 w-full justify-start font-normal"
          data-testid={`${testid}-trigger`}
        >
          <CalendarIcon className="w-4 h-4 mr-2 text-slate-500" />
          <span className="capitalize">{display}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3 border-b border-slate-100 text-xs text-slate-600 flex items-center gap-4">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded bg-red-500"></span>
            Lleno / festivo
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded bg-blue-700"></span>
            Seleccionado
          </span>
        </div>
        <Calendar
          mode="single"
          locale={es}
          weekStartsOn={1}
          selected={selectedDate}
          onSelect={(d) => {
            if (!d) return;
            const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            onChange(iso);
            setOpen(false);
          }}
          modifiers={{ full: fullDates }}
          modifiersClassNames={{ full: "bg-red-100 text-red-700 line-through hover:bg-red-200" }}
          fromDate={new Date()}
          data-testid={testid}
        />
      </PopoverContent>
    </Popover>
  );
}
